import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { 
  Users, UserPlus, Mail, Phone, MapPin, TrendingUp, TrendingDown, Target,
  DollarSign, Calendar, Award, Edit, Trash2, Plus, Search, Filter,
  RefreshCw, AlertCircle, CheckCircle, X, Eye, BarChart3, Clock,
  User, Crown, Medal, Star, Flame, Power, Save, UserX, Key, Minus, Gem
} from 'lucide-react';

// Icône de badge : Gem (Diamant/Saphir) ou Medal (Or/Argent/Bronze), colorée
const BadgeIcon = ({ badge, size = 16 }) => {
  const Cmp = badge.icon === 'diamond' ? Gem : Medal;
  return <Cmp style={{ width: size, height: size, color: badge.color }} />;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================================
// DESIGN PILOT — constantes de style
// ============================================================
const T = {
  turq: '#1FD1FD', turq2: '#5DDEFF', turqStrong: '#0E7A99',
  ink: '#1A1F36', ink2: '#4A5568', ink3: '#697386',
  border: 'rgba(227,232,238,1)', bg: '#F6F8FB',
  orange: '#EF9F27', green: '#16A34A', red: '#DC2626',
};

// Barème badges basé sur le NOMBRE DE VENTES (gagnes)
// icon: 'diamond' (Diamant/Saphir) ou 'medal' (Or/Argent) ou 'medal2' (Bronze)
const getBadge = (ventes) => {
  const v = Number(ventes) || 0;
  if (v >= 9)  return { label: 'Saphir',  color: '#7F77DD', icon: 'diamond' };
  if (v >= 6)  return { label: 'Diamant', color: '#378ADD', icon: 'diamond' };
  if (v >= 4)  return { label: 'Or',      color: '#EF9F27', icon: 'medal' };
  if (v >= 2)  return { label: 'Argent',  color: '#888780', icon: 'medal' };
  return            { label: 'Bronze',  color: '#B4B2A9', icon: 'medal2' };
};

const Commerciaux = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('apercu');
  const [commerciaux, setCommerciaux] = useState([]);
  const [statsParId, setStatsParId] = useState({});      // stats par commercial (ventes, leads, conv)
  const [classement, setClassement] = useState([]);       // /classement/all
  const [objectifs, setObjectifs] = useState({});         // objectif record + progression par id
  const [statistiques, setStatistiques] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCommercial, setSelectedCommercial] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [periode, setPeriode] = useState('mois');
  const [formData, setFormData] = useState({
    nom: '', prenom: '', email: '', password: '', telephone: '',
    role: 'commercial', secteur: '', objectif_mensuel: '', commission: '',
    adresse: '', code_postal: '', email_perso: '', actif: true
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

      // 1) Liste des commerciaux (users)
      const usersResponse = await fetch(`${API}/users`, { headers });
      let commerciauxData = [];
      if (usersResponse.ok) {
        const response = await usersResponse.json();
        const users = response.data?.users || response.users || response.data || response || [];
        commerciauxData = users.filter(u =>
          ['commercial', 'manager_vente', 'employe_vente'].includes(u.role)
        );
        setCommerciaux(commerciauxData);
      } else {
        setError('Erreur lors du chargement des commerciaux');
      }

      // 2) Stats par commercial (ventes, leads, conversion) — /stats/commercials
      try {
        const sResp = await fetch(`${API}/stats/commercials`, { headers });
        if (sResp.ok) {
          const sJson = await sResp.json();
          const list = sJson.data?.commercials || [];
          const map = {};
          list.forEach(c => { map[c.id] = c; });
          setStatsParId(map);
        }
      } catch (e) { /* stats non bloquantes */ }

      // 3) Classement du mois — /dashboard-commercial/classement/all
      try {
        const cResp = await fetch(`${API}/dashboard-commercial/classement/all`, { headers });
        if (cResp.ok) {
          const cJson = await cResp.json();
          setClassement(cJson.data?.classement || []);
        }
      } catch (e) { /* classement non bloquant */ }

      // 4) Objectif "record perso" + progression, pour chaque commercial — /dashboard-commercial/{id}
      try {
        const objMap = {};
        await Promise.all(commerciauxData.map(async (c) => {
          try {
            const oResp = await fetch(`${API}/dashboard-commercial/${c.id}`, { headers });
            if (oResp.ok) {
              const oJson = await oResp.json();
              const d = oJson.data || oJson;
              objMap[c.id] = {
                objectif: d.objectif_mois ?? 0,
                poses: d.poses_mois ?? 0,
                pourcentage: d.pourcentage ?? 0
              };
            }
          } catch (e) { /* un commercial qui échoue ne bloque pas */ }
        }));
        setObjectifs(objMap);
      } catch (e) { /* objectifs non bloquants */ }

      setError(null);
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Stats globales recalculées dès qu'on a les données
  useEffect(() => {
    if (!commerciaux.length) { setStatistiques(null); return; }
    const totalVentes = Object.values(statsParId).reduce((s, c) => s + (c.gagnes || 0), 0);
    const caEquipe = Object.values(statsParId).reduce((s, c) => s + (c.ca || 0), 0);
    const actifs = commerciaux.filter(c => c.actif).length;
    // progression équipe = moyenne des pourcentages d'objectif
    const pcts = Object.values(objectifs).map(o => o.pourcentage || 0);
    const objEquipe = pcts.length ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length) : 0;
    setStatistiques({
      total_commerciaux: commerciaux.length,
      actifs,
      ventes_total: totalVentes,
      ca_equipe: caEquipe,
      objectif_equipe: objEquipe
    });
  }, [commerciaux, statsParId, objectifs]);

  // ---------- Fonctions backend (réutilisées telles quelles) ----------
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom || !formData.prenom || !formData.email || !formData.password) {
      setError('Veuillez remplir tous les champs obligatoires (nom, prénom, email, mot de passe)');
      return;
    }
    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email, password: formData.password,
          nom: formData.nom, prenom: formData.prenom, role: formData.role,
          telephone: formData.telephone || undefined, actif: formData.actif
        })
      });
      if (response.ok) {
        await fetchData();
        setShowAddForm(false);
        setFormData({
          nom: '', prenom: '', email: '', password: '', telephone: '',
          role: 'commercial', secteur: '', objectif_mensuel: '', commission: '',
          adresse: '', code_postal: '', email_perso: '', actif: true
        });
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Erreur lors de la création du commercial');
      }
    } catch (err) { setError('Erreur de connexion'); }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'manager_vente': return 'Resp. Vente';
      case 'commercial': return 'Commercial';
      case 'employe_vente': return 'Commercial';
      default: return role;
    }
  };

  const handleDeleteCommercial = async (commercialId, commercialNom) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users/${commercialId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) { await fetchData(); setError(null); }
      else { setError('Erreur lors de la suppression du commercial'); }
    } catch (err) { setError('Erreur de connexion lors de la suppression'); }
  };

  const handleToggleActive = async (commercialId, currentStatus) => {
    setCommerciaux(prev => prev.map(c => c.id === commercialId ? { ...c, actif: !currentStatus } : c));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users/${commercialId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ actif: !currentStatus })
      });
      if (response.ok) { setError(null); }
      else {
        setCommerciaux(prev => prev.map(c => c.id === commercialId ? { ...c, actif: currentStatus } : c));
        setError('Erreur lors de la modification du statut');
      }
    } catch (err) {
      setCommerciaux(prev => prev.map(c => c.id === commercialId ? { ...c, actif: currentStatus } : c));
      setError('Erreur de connexion');
    }
  };

  const [editingCommercial, setEditingCommercial] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [commercialToDelete, setCommercialToDelete] = useState(null); // pop-up confirmation suppression

  const openEdit = (commercial) => {
    setEditingCommercial(commercial);
    setEditFormData({
      nom: commercial.nom, prenom: commercial.prenom, email: commercial.email,
      telephone: commercial.telephone || '', role: commercial.role, actif: commercial.actif
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users/${editingCommercial.id}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editFormData)
      });
      if (response.ok) {
        await fetchData(); setEditingCommercial(null); setEditFormData({}); setError(null);
      } else { setError('Erreur lors de la modification du commercial'); }
    } catch (err) { setError('Erreur de connexion'); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Les mots de passe ne correspondent pas'); return; }
    if (newPassword.length < 6) { setError('Le mot de passe doit contenir au moins 6 caractères'); return; }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users/${editingCommercial.id}/change-password`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword })
      });
      if (response.ok) {
        setShowPasswordModal(false); setNewPassword(''); setConfirmPassword(''); setError(null);
      } else {
        const data = await response.json();
        setError(data.detail || 'Erreur lors de la modification du mot de passe');
      }
    } catch (err) { setError('Erreur de connexion'); }
  };

  // Helpers d'affichage par commercial
  const ventesDe = (c) => statsParId[c.id]?.gagnes ?? 0;
  const leadsDe  = (c) => statsParId[c.id]?.total_assigned ?? 0;
  const caDe     = (c) => statsParId[c.id]?.ca ?? 0;
  const fmtEuro  = (n) => `${(Number(n) || 0).toLocaleString('fr-FR')} €`;
  const convDe   = (c) => statsParId[c.id]?.taux_conversion ?? 0;
  const objDe    = (c) => objectifs[c.id]?.objectif ?? 0;
  const posesDe  = (c) => objectifs[c.id]?.poses ?? 0;
  const pctDe    = (c) => objectifs[c.id]?.pourcentage ?? 0;

  const filteredCommerciaux = commerciaux.filter(c =>
    `${c.prenom} ${c.nom}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && !commerciaux.length) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-7 h-7 animate-spin mr-3" style={{ color: T.turq }} />
        <span style={{ fontSize: 15, color: T.ink2 }}>Chargement des commerciaux…</span>
      </div>
    );
  }

  const tabs = [
    { id: 'apercu', label: "Vue d'ensemble" },
    { id: 'classement', label: 'Classement' },
    { id: 'performance', label: 'Performance' },
    { id: 'gestion', label: "Gestion d'équipe" },
  ];

  // Badge : icône colorée + nom (sans pilule, comme la maquette validée)
  const BadgePill = ({ ventes }) => {
    const b = getBadge(ventes);
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: T.ink2 }}>
        <BadgeIcon badge={b} size={16} />
        {b.label}
      </span>
    );
  };

  // Barre d'objectif avec ligne chiffrée "X / Y poses" + % (turquoise si OK, orange si < 50%)
  const ObjectifBar = ({ pct, poses, objectif, showText = true }) => {
    const p = Math.min(100, Math.max(0, pct || 0));
    const col = p < 50 ? T.orange : T.turq;
    return (
      <div>
        {showText && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 10.5, color: T.ink3, marginBottom: 4 }}>
            <span>{poses ?? 0} / {objectif ?? 0} poses</span>
            <span>{p}%</span>
          </div>
        )}
        <div style={{ height: 7, borderRadius: 100, background: '#EEF2F6', overflow: 'hidden' }}>
          <div style={{ width: `${p}%`, height: '100%', borderRadius: 100, background: col, transition: 'width .3s' }} />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" style={{ fontFamily: 'Inter, sans-serif' }}>
      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 12, padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertCircle className="w-5 h-5" style={{ color: T.red }} />
          <span style={{ color: '#991B1B', fontSize: 13 }}>{error}</span>
        </div>
      )}

      {/* Onglets */}
      <div style={{ display: 'flex', gap: 6, borderBottom: `1px solid ${T.border}`, paddingBottom: 0 }}>
        {tabs.map(tab => {
          const on = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: 13.5, fontWeight: on ? 600 : 500, color: on ? T.turqStrong : T.ink3,
                borderBottom: on ? `2px solid ${T.turq}` : '2px solid transparent', marginBottom: -1,
                transition: 'all .15s' }}>
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ================= ONGLET 1 : VUE D'ENSEMBLE ================= */}
      {activeTab === 'apercu' && (
        <div className="space-y-5">
          {/* Bandeau 4 stats */}
          {statistiques && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
              <StatCard icon={<Users style={{ width: 15, height: 15, color: T.turq }} />} label="Total commerciaux" value={statistiques.total_commerciaux} />
              <StatCard icon={<CheckCircle style={{ width: 15, height: 15, color: T.turq }} />} label="Actifs" value={statistiques.actifs} />
              <StatCard icon={<DollarSign style={{ width: 15, height: 15, color: T.turq }} />} label="CA équipe" value={fmtEuro(statistiques.ca_equipe)} />
              <StatCard icon={<Award style={{ width: 15, height: 15, color: T.turq }} />} label="Ventes signées" value={statistiques.ventes_total} />
              <StatCard icon={<Target style={{ width: 15, height: 15, color: T.turq }} />} label="Objectif équipe" value={`${statistiques.objectif_equipe}%`} />
            </div>
          )}

          {/* Recherche */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: `1px solid ${T.border}`,
            borderRadius: 100, padding: '8px 16px', maxWidth: 380 }}>
            <Search style={{ width: 15, height: 15, color: T.ink3 }} />
            <input type="text" placeholder="Rechercher un commercial…" value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: T.ink }} />
          </div>

          {/* Liste en colonnes alignées */}
          <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
            {/* En-tête colonnes */}
            <div style={{ display: 'grid', gridTemplateColumns: '2.2fr 2fr 1.2fr 1.2fr', gap: 12,
              padding: '12px 20px', borderBottom: `1px solid ${T.border}`, background: '#FAFBFC' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.ink3, textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left' }}>Commercial</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.ink3, textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'center' }}>Objectif</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.ink3, textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'center' }}>Statut</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.ink3, textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'center' }}>Badge</span>
            </div>
            {filteredCommerciaux.length > 0 ? filteredCommerciaux.map(c => (
              <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '2.2fr 2fr 1.2fr 1.2fr', gap: 12,
                padding: '14px 20px', borderBottom: `1px solid ${T.border}`, alignItems: 'center' }}>
                {/* Commercial (sans avatar) */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {c.prenom} {c.nom}
                  </div>
                  <div style={{ fontSize: 11.5, color: T.ink3 }}>{ventesDe(c)} ventes · {fmtEuro(caDe(c))}</div>
                </div>
                {/* Objectif chiffré */}
                <div><ObjectifBar pct={pctDe(c)} poses={posesDe(c)} objectif={objDe(c)} /></div>
                {/* Statut (centré) */}
                <div style={{ textAlign: 'center' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 100,
                    fontSize: 12, fontWeight: 500, background: c.actif ? '#E7FAF0' : '#F1F3F5', color: c.actif ? T.green : T.ink3 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.actif ? T.green : '#B4B2A9' }} />
                    {c.actif ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                {/* Badge (centré) */}
                <div style={{ textAlign: 'center' }}><BadgePill ventes={ventesDe(c)} /></div>
              </div>
            )) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: T.ink3, fontSize: 13 }}>
                {searchTerm ? 'Aucun commercial trouvé pour cette recherche' : 'Aucun commercial trouvé'}
              </div>
            )}
          </div>

          {/* Barème des badges */}
          <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: '16px 20px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 12 }}>Barème des badges (sur le nombre de ventes)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
              {[
                { label: 'Bronze', color: '#B4B2A9', icon: 'medal2', range: '0–1' },
                { label: 'Argent', color: '#888780', icon: 'medal', range: '2–3' },
                { label: 'Or', color: '#EF9F27', icon: 'medal', range: '4–5' },
                { label: 'Diamant', color: '#378ADD', icon: 'diamond', range: '6–8' },
                { label: 'Saphir', color: '#7F77DD', icon: 'diamond', range: '9+' },
              ].map(b => (
                <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <BadgeIcon badge={b} size={15} />
                  <span style={{ fontSize: 12.5, color: T.ink2, fontWeight: 500 }}>{b.label}</span>
                  <span style={{ fontSize: 12, color: T.ink3 }}>{b.range}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ================= ONGLET 2 : CLASSEMENT ================= */}
      {activeTab === 'classement' && (
        <OngletClassement commerciaux={commerciaux} statsParId={statsParId} classement={classement}
          getBadge={getBadge} T={T} BadgePill={BadgePill} />
      )}

      {/* ================= ONGLET 3 : PERFORMANCE ================= */}
      {activeTab === 'performance' && (
        <OngletPerformance commerciaux={commerciaux} statsParId={statsParId} objectifs={objectifs}
          periode={periode} setPeriode={setPeriode} T={T} ObjectifBar={ObjectifBar} />
      )}

      {/* ================= ONGLET 4 : GESTION D'ÉQUIPE ================= */}
      {activeTab === 'gestion' && (
        <div className="space-y-5">
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => setShowAddForm(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 100,
                border: 'none', cursor: 'pointer', background: T.turq, color: '#fff', fontSize: 13, fontWeight: 600 }}>
              <Plus className="w-4 h-4" /> Ajouter un commercial
            </button>
          </div>
          <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2.2fr 2.2fr 1.2fr 1.2fr', gap: 12,
              padding: '12px 20px', borderBottom: `1px solid ${T.border}`, background: '#FAFBFC' }}>
              <span style={hCol(T)}>Commercial</span>
              <span style={hCol(T)}>Contact</span>
              <span style={{ ...hCol(T), textAlign: 'center' }}>Statut</span>
              <span style={{ ...hCol(T), textAlign: 'right' }}>Actions</span>
            </div>
            {commerciaux.map(c => (
              <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '2.2fr 2.2fr 1.2fr 1.2fr', gap: 12,
                padding: '14px 20px', borderBottom: `1px solid ${T.border}`, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                    background: `linear-gradient(135deg, ${T.turq}, ${T.turq2})`, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>
                    {c.prenom?.charAt(0)}{c.nom?.charAt(0)}
                  </span>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>{c.prenom} {c.nom}</div>
                    <div style={{ fontSize: 11.5, color: T.ink3 }}>{getRoleLabel(c.role)}</div>
                  </div>
                </div>
                <div style={{ fontSize: 12.5, color: T.ink2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Mail style={{ width: 13, height: 13, color: T.ink3 }} />{c.email}</div>
                  {c.telephone && <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}><Phone style={{ width: 13, height: 13, color: T.ink3 }} />{c.telephone}</div>}
                </div>
                {/* Statut : titre + toggle + label */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <button onClick={() => handleToggleActive(c.id, c.actif)}
                    style={{ position: 'relative', width: 38, height: 21, borderRadius: 100, border: 'none', cursor: 'pointer',
                      background: c.actif ? T.turq : '#D1D5DB', transition: 'background .2s' }}>
                    <span style={{ position: 'absolute', top: 3, left: c.actif ? 20 : 3, width: 15, height: 15,
                      borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
                  </button>
                  <span style={{ fontSize: 11, fontWeight: 500, color: c.actif ? T.green : T.ink3 }}>{c.actif ? 'Actif' : 'Inactif'}</span>
                </div>
                {/* Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                  <IconBtn title="Modifier" onClick={() => openEdit(c)} color={T.ink2}><Edit className="w-4 h-4" /></IconBtn>
                  <IconBtn title="Changer le mot de passe" onClick={() => { openEdit(c); setShowPasswordModal(true); }} color={T.ink2}><Key className="w-4 h-4" /></IconBtn>
                  <IconBtn title="Supprimer" onClick={() => setCommercialToDelete(c)} color={T.red}><Trash2 className="w-4 h-4" /></IconBtn>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============ MODALS (réutilisés) ============ */}
      {showAddForm && (
        <ModalAdd formData={formData} handleInputChange={handleInputChange}
          handleFormSubmit={handleFormSubmit} onClose={() => setShowAddForm(false)} T={T} />
      )}
      {editingCommercial && !showPasswordModal && (
        <ModalEdit editingCommercial={editingCommercial} editFormData={editFormData} setEditFormData={setEditFormData}
          handleEditSubmit={handleEditSubmit} onClose={() => { setEditingCommercial(null); setEditFormData({}); }}
          onOpenPassword={() => setShowPasswordModal(true)} T={T} />
      )}
      {showPasswordModal && editingCommercial && (
        <ModalPassword editingCommercial={editingCommercial} newPassword={newPassword} setNewPassword={setNewPassword}
          confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword} showPassword={showPassword}
          setShowPassword={setShowPassword} handleChangePassword={handleChangePassword}
          onClose={() => { setShowPasswordModal(false); setNewPassword(''); setConfirmPassword(''); setShowPassword(false); }} T={T} />
      )}
      {commercialToDelete && (
        <ModalConfirmDelete commercial={commercialToDelete} T={T}
          onCancel={() => setCommercialToDelete(null)}
          onConfirm={async () => {
            await handleDeleteCommercial(commercialToDelete.id, `${commercialToDelete.prenom} ${commercialToDelete.nom}`);
            setCommercialToDelete(null);
          }} />
      )}
    </div>
  );
};

// ============================================================
// Sous-composants
// ============================================================
const hCol = (T) => ({ fontSize: 11, fontWeight: 600, color: T.ink3, textTransform: 'uppercase', letterSpacing: '0.4px' });

const StatCard = ({ icon, label, value }) => (
  <div style={{ background: '#fff', border: '1px solid rgba(227,232,238,1)', borderRadius: 16, padding: '14px 16px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
      {icon}
      <span style={{ fontSize: 11.5, color: '#697386', fontWeight: 500 }}>{label}</span>
    </div>
    <div style={{ fontSize: 24, fontWeight: 700, color: '#1A1F36', letterSpacing: '-0.5px' }}>{value}</div>
  </div>
);

const IconBtn = ({ children, title, onClick, color }) => (
  <button title={title} onClick={onClick}
    style={{ width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer', background: 'transparent',
      color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    onMouseEnter={(e) => e.currentTarget.style.background = '#F1F3F5'}
    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
    {children}
  </button>
);

// ---- Onglet Classement ----
const OngletClassement = ({ commerciaux, statsParId, classement, getBadge, T, BadgePill }) => {
  // On combine classement (poses du mois + position) avec les infos commerciaux
  const byId = {}; commerciaux.forEach(c => { byId[c.id] = c; });
  const rows = (classement || []).map(item => {
    const c = byId[item.id] || { prenom: item.prenom, nom: item.nom, id: item.id };
    const ventes = statsParId[item.id]?.gagnes ?? 0;
    const ca = statsParId[item.id]?.ca ?? 0;
    return { ...c, poses: item.poses ?? 0, position: item.position, ventes, ca };
  });
  const fmtEuro = (n) => `${(Number(n) || 0).toLocaleString('fr-FR')} €`;
  const top3 = rows.slice(0, 3);            // déjà triés par position
  const medailles = ['🥇', '🥈', '🥉'];

  return (
    <div className="space-y-5">
      {/* Podium — ordre 1-2-3, sans avatar, noms complets, même taille */}
      {top3.length > 0 && (
        <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: '18px 20px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Crown style={{ width: 16, height: 16, color: T.turq }} /> Podium du mois
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {top3.map((c, i) => (
              <div key={c.id} style={{ background: T.bg, borderRadius: 12, padding: '16px 8px', textAlign: 'center' }}>
                <div style={{ fontSize: 26 }}>{medailles[i]}</div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink, marginTop: 10 }}>{c.prenom} {c.nom}</div>
                <div style={{ fontSize: 12.5, color: T.ink3 }}>{fmtEuro(c.ca)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Classement complet — sans titre, sans #, sans avatar, badge icône, colonnes centrées */}
      <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2.2fr 1.3fr 0.9fr 0.9fr 1.1fr', gap: 12,
          padding: '12px 20px', borderBottom: `1px solid ${T.border}`, background: '#FAFBFC' }}>
          <span style={{ ...hCol(T), textAlign: 'left' }}>Commercial</span>
          <span style={{ ...hCol(T), textAlign: 'center' }}>CA</span>
          <span style={{ ...hCol(T), textAlign: 'center' }}>Poses</span>
          <span style={{ ...hCol(T), textAlign: 'center' }}>Ventes</span>
          <span style={{ ...hCol(T), textAlign: 'center' }}>Badge</span>
        </div>
        {rows.map(c => (
          <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '2.2fr 1.3fr 0.9fr 0.9fr 1.1fr', gap: 12,
            padding: '13px 20px', borderBottom: `1px solid ${T.border}`, alignItems: 'center' }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>{c.prenom} {c.nom}</span>
            <span style={{ fontSize: 13, color: T.ink2, textAlign: 'center', fontWeight: 600 }}>{fmtEuro(c.ca)}</span>
            <span style={{ fontSize: 13, color: T.ink2, textAlign: 'center', fontWeight: 600 }}>{c.poses}</span>
            <span style={{ fontSize: 13, color: T.ink2, textAlign: 'center' }}>{c.ventes}</span>
            <div style={{ textAlign: 'center' }}><BadgePill ventes={c.ventes} /></div>
          </div>
        ))}
        {rows.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: T.ink3, fontSize: 13 }}>Aucune donnée de classement</div>
        )}
      </div>
    </div>
  );
};

// ---- Onglet Performance ----
const OngletPerformance = ({ commerciaux, statsParId, objectifs, periode, setPeriode, T, ObjectifBar }) => {
  const rows = commerciaux.map(c => {
    const s = statsParId[c.id] || {};
    const ventes = s.gagnes ?? 0;
    const leads = s.total_assigned ?? 0;
    const conv = s.taux_conversion ?? 0;
    const pct = objectifs[c.id]?.pourcentage ?? 0;
    const poses = objectifs[c.id]?.poses ?? 0;
    const obj = objectifs[c.id]?.objectif ?? 0;
    return { ...c, ventes, leads, conv, pct, poses, obj };
  });

  const convColor = (v) => v >= 30 ? T.green : v >= 15 ? T.orange : T.red;

  return (
    <div className="space-y-5">
      {/* Filtre période */}
      <div style={{ display: 'flex', gap: 6 }}>
        {[{ id: 'mois', l: 'Ce mois' }, { id: 'trimestre', l: 'Trimestre' }, { id: 'annee', l: 'Année' }].map(p => {
          const on = periode === p.id;
          return (
            <button key={p.id} onClick={() => setPeriode(p.id)}
              style={{ padding: '7px 16px', borderRadius: 100, border: `1px solid ${on ? T.turq : T.border}`,
                background: on ? T.turq : '#fff', color: on ? '#fff' : T.ink2, fontSize: 12.5, fontWeight: 500, cursor: 'pointer' }}>
              {p.l}
            </button>
          );
        })}
      </div>
      {/* Tableau (sans titre, sans avatar, colonnes centrées, objectif chiffré) */}
      <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2.2fr 1fr 1fr 1.2fr 1.6fr', gap: 12,
          padding: '12px 20px', borderBottom: `1px solid ${T.border}`, background: '#FAFBFC' }}>
          <span style={{ ...hCol(T), textAlign: 'left' }}>Commercial</span>
          <span style={{ ...hCol(T), textAlign: 'center' }}>Leads</span>
          <span style={{ ...hCol(T), textAlign: 'center' }}>Ventes</span>
          <span style={{ ...hCol(T), textAlign: 'center' }}>Conversion</span>
          <span style={{ ...hCol(T), textAlign: 'center' }}>Objectif</span>
        </div>
        {rows.map(c => (
          <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '2.2fr 1fr 1fr 1.2fr 1.6fr', gap: 12,
            padding: '13px 20px', borderBottom: `1px solid ${T.border}`, alignItems: 'center' }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>{c.prenom} {c.nom}</span>
            <span style={{ fontSize: 13, color: T.ink2, textAlign: 'center' }}>{c.leads}</span>
            <span style={{ fontSize: 13, color: T.ink2, textAlign: 'center' }}>{c.ventes}</span>
            <div style={{ textAlign: 'center' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: convColor(c.conv) }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: convColor(c.conv) }} />{c.conv}%
              </span>
            </div>
            <ObjectifBar pct={c.pct} poses={c.poses} objectif={c.obj} />
          </div>
        ))}
        {rows.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: T.ink3, fontSize: 13 }}>Aucune donnée</div>
        )}
      </div>
    </div>
  );
};

// ---- Modal AJOUT ----
const ModalAdd = ({ formData, handleInputChange, handleFormSubmit, onClose, T }) => (
  <div style={ovl()}>
    <div style={{ ...box(640), maxHeight: '90vh', overflowY: 'auto' }}>
      <div style={head(T)}>
        <h3 style={ttl(T)}>Ajouter un commercial</h3>
        <button onClick={onClose} style={xBtn}><X className="w-5 h-5" style={{ color: T.ink3 }} /></button>
      </div>
      <form onSubmit={handleFormSubmit} style={{ padding: 22 }} className="space-y-4">
        <div style={grid2}>
          <Field label="Prénom *"><input style={inp(T)} value={formData.prenom} required
            onChange={(e) => handleInputChange('prenom', e.target.value)} placeholder="Prénom" /></Field>
          <Field label="Nom *"><input style={inp(T)} value={formData.nom} required
            onChange={(e) => handleInputChange('nom', e.target.value)} placeholder="Nom" /></Field>
        </div>
        <div style={grid2}>
          <Field label="Email personnel"><input style={inp(T)} type="email" value={formData.email_perso || ''}
            onChange={(e) => handleInputChange('email_perso', e.target.value)} placeholder="jean@gmail.com" /></Field>
          <Field label="Téléphone"><input style={inp(T)} value={formData.telephone}
            onChange={(e) => handleInputChange('telephone', e.target.value)} placeholder="06 12 34 56 78" /></Field>
        </div>
        <Field label="Email professionnel *"><input style={inp(T)} type="email" value={formData.email} required
          onChange={(e) => handleInputChange('email', e.target.value)} placeholder="jean@eco-enr.fr" /></Field>
        <Field label="Mot de passe *"><input style={inp(T)} type="password" value={formData.password} required
          onChange={(e) => handleInputChange('password', e.target.value)} placeholder="Min. 6 caractères" /></Field>
        <div style={grid2}>
          <Field label="Rôle">
            <select style={inp(T)} value={formData.role} onChange={(e) => handleInputChange('role', e.target.value)}>
              <option value="commercial">Commercial</option>
              <option value="manager_vente">Responsable Vente</option>
            </select>
          </Field>
          <Field label="Statut">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 40 }}>
              <button type="button" onClick={() => handleInputChange('actif', !formData.actif)}
                style={{ position: 'relative', width: 44, height: 24, borderRadius: 100, border: 'none', cursor: 'pointer',
                  background: formData.actif ? T.turq : '#D1D5DB' }}>
                <span style={{ position: 'absolute', top: 3, left: formData.actif ? 23 : 3, width: 18, height: 18,
                  borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
              </button>
              <span style={{ fontSize: 13, fontWeight: 500, color: formData.actif ? T.green : T.ink3 }}>{formData.actif ? 'Actif' : 'Inactif'}</span>
            </div>
          </Field>
        </div>
        <div style={foot(T)}>
          <button type="button" onClick={onClose} style={btnGhost(T)}>Annuler</button>
          <button type="submit" style={btnPrim(T)}>Créer le commercial</button>
        </div>
      </form>
    </div>
  </div>
);

// ---- Modal MODIFICATION ----
const ModalEdit = ({ editingCommercial, editFormData, setEditFormData, handleEditSubmit, onClose, onOpenPassword, T }) => (
  <div style={ovl()}>
    <div style={{ ...box(540), maxHeight: '90vh', overflowY: 'auto' }}>
      <div style={head(T)}>
        <h3 style={ttl(T)}>Modifier {editingCommercial.prenom} {editingCommercial.nom}</h3>
        <button onClick={onClose} style={xBtn}><X className="w-5 h-5" style={{ color: T.ink3 }} /></button>
      </div>
      <form onSubmit={handleEditSubmit} style={{ padding: 22 }} className="space-y-4">
        <div style={grid2}>
          <Field label="Prénom"><input style={inp(T)} required value={editFormData.prenom || ''}
            onChange={(e) => setEditFormData(p => ({ ...p, prenom: e.target.value }))} /></Field>
          <Field label="Nom"><input style={inp(T)} required value={editFormData.nom || ''}
            onChange={(e) => setEditFormData(p => ({ ...p, nom: e.target.value }))} /></Field>
        </div>
        <Field label="Email professionnel"><input style={inp(T)} type="email" required value={editFormData.email || ''}
          onChange={(e) => setEditFormData(p => ({ ...p, email: e.target.value }))} /></Field>
        <Field label="Téléphone"><input style={inp(T)} value={editFormData.telephone || ''}
          onChange={(e) => setEditFormData(p => ({ ...p, telephone: e.target.value }))} /></Field>
        <div style={grid2}>
          <Field label="Rôle">
            <select style={inp(T)} value={editFormData.role || 'commercial'}
              onChange={(e) => setEditFormData(p => ({ ...p, role: e.target.value }))}>
              <option value="commercial">Commercial</option>
              <option value="manager_vente">Responsable Vente</option>
            </select>
          </Field>
          <Field label="Statut">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 40 }}>
              <button type="button" onClick={() => setEditFormData(p => ({ ...p, actif: !p.actif }))}
                style={{ position: 'relative', width: 44, height: 24, borderRadius: 100, border: 'none', cursor: 'pointer',
                  background: editFormData.actif ? T.turq : '#D1D5DB' }}>
                <span style={{ position: 'absolute', top: 3, left: editFormData.actif ? 23 : 3, width: 18, height: 18,
                  borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
              </button>
              <span style={{ fontSize: 13, fontWeight: 500, color: editFormData.actif ? T.green : T.ink3 }}>{editFormData.actif ? 'Actif' : 'Inactif'}</span>
            </div>
          </Field>
        </div>
        {/* Accès CRM */}
        <div style={{ background: '#F0FBFE', border: `1px solid ${T.turq2}`, borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: T.turqStrong, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Mail style={{ width: 14, height: 14 }} /> Accès CRM (visible admin uniquement)
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12.5, color: T.ink3 }}>Email :</span>
            <span style={{ fontSize: 12.5, fontWeight: 500, color: T.ink }}>{editingCommercial.email}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12.5, color: T.ink3 }}>Mot de passe :</span>
            <button type="button" onClick={onOpenPassword}
              style={{ fontSize: 12.5, color: T.turqStrong, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
              Modifier
            </button>
          </div>
        </div>
        <div style={foot(T)}>
          <button type="button" onClick={onClose} style={btnGhost(T)}>Annuler</button>
          <button type="submit" style={{ ...btnPrim(T), display: 'flex', alignItems: 'center', gap: 6 }}><Save className="w-4 h-4" /> Sauvegarder</button>
        </div>
      </form>
    </div>
  </div>
);

// ---- Modal MOT DE PASSE ----
const ModalPassword = ({ editingCommercial, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
  showPassword, setShowPassword, handleChangePassword, onClose, T }) => (
  <div style={{ ...ovl(), zIndex: 60 }}>
    <div style={box(440)}>
      <div style={head(T)}>
        <h3 style={ttl(T)}>Changer le mot de passe</h3>
        <button onClick={onClose} style={xBtn}><X className="w-5 h-5" style={{ color: T.ink3 }} /></button>
      </div>
      <form onSubmit={handleChangePassword} style={{ padding: 22 }} className="space-y-4">
        <div style={{ background: '#F0FBFE', borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{editingCommercial.prenom} {editingCommercial.nom}</div>
          <div style={{ fontSize: 12.5, color: T.ink3 }}>{editingCommercial.email}</div>
        </div>
        <Field label="Nouveau mot de passe">
          <div style={{ position: 'relative' }}>
            <input style={inp(T)} type={showPassword ? 'text' : 'password'} required minLength={6}
              value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min. 6 caractères" />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}>
              <Eye style={{ width: 16, height: 16, color: T.ink3 }} />
            </button>
          </div>
        </Field>
        <Field label="Confirmer le mot de passe">
          <input style={inp(T)} type={showPassword ? 'text' : 'password'} required minLength={6}
            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirmer" />
        </Field>
        <div style={foot(T)}>
          <button type="button" onClick={onClose} style={btnGhost(T)}>Annuler</button>
          <button type="submit" style={{ ...btnPrim(T), display: 'flex', alignItems: 'center', gap: 6 }}><Save className="w-4 h-4" /> Changer</button>
        </div>
      </form>
    </div>
  </div>
);

// ---- Helpers UI partagés pour les modals ----
const Field = ({ label, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: 12.5, fontWeight: 500, color: '#4A5568', marginBottom: 6 }}>{label}</label>
    {children}
  </div>
);
const ovl = () => ({ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 });
const box = (w) => ({ background: '#fff', borderRadius: 16, width: '100%', maxWidth: w });
const head = (T) => ({ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '18px 22px', borderBottom: `1px solid ${T.border}` });
const ttl = (T) => ({ fontSize: 16, fontWeight: 600, color: T.ink, margin: 0 });
const xBtn = { background: 'none', border: 'none', cursor: 'pointer', padding: 6 };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 };
const inp = (T) => ({ width: '100%', padding: '9px 12px', borderRadius: 10, border: `1px solid ${T.border}`,
  fontSize: 13, color: T.ink, outline: 'none', boxSizing: 'border-box', fontFamily: 'Inter, sans-serif' });
const foot = (T) => ({ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 16, borderTop: `1px solid ${T.border}`, marginTop: 4 });
const btnGhost = (T) => ({ padding: '9px 18px', borderRadius: 100, border: `1px solid ${T.border}`,
  background: '#fff', color: T.ink2, fontSize: 13, fontWeight: 500, cursor: 'pointer' });
const btnPrim = (T) => ({ padding: '9px 18px', borderRadius: 100, border: 'none',
  background: T.turq, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' });

// ---- Modal CONFIRMATION SUPPRESSION ----
const ModalConfirmDelete = ({ commercial, onCancel, onConfirm, T }) => (
  <div style={{ ...ovl(), zIndex: 60 }}>
    <div style={{ ...box(380), textAlign: 'center', padding: 24 }}>
      <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#FCEBEB', color: T.red,
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
        <Trash2 className="w-6 h-6" />
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: T.ink, marginBottom: 6 }}>Supprimer ce commercial ?</div>
      <p style={{ fontSize: 13, color: T.ink3, margin: '0 0 20px' }}>
        Es-tu sûr de vouloir supprimer <strong>{commercial.prenom} {commercial.nom}</strong> ? Cette action est définitive.
      </p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        <button onClick={onCancel} style={btnGhost(T)}>Annuler</button>
        <button onClick={onConfirm} style={{ padding: '9px 18px', borderRadius: 100, border: 'none',
          background: T.red, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Supprimer</button>
      </div>
    </div>
  </div>
);

export default Commerciaux;
